import { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { Spinner, Title, Modal, Table } from "components";
import { createManufacturer, getManufacturers, updateManufacturersField, getManufacturerDetails, updateManufacturer, cleanManufacturersDetails, getManufacturerStock, importStockToManufacturers } from "redux/slices/ManagedManufacturers";
import { ZTextInput } from "components/ZTextInput";
import { ZLabel } from "components/ZLabel";
import { ZTooltip } from "components/ZTooltip";
import { ManagedStocksPopup } from "popups/ManagedStocks/ManagedStocks";
import { cleanStockDetails } from "redux/slices/Stocks";
import { useStockFilter } from "hooks";
import { getRowIds } from "helpers/data/dataHandling";

export function NewManufacture() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const getStockFilterOptions = useStockFilter();
	const { manufacturerId } = useParams<string>();
	const manufacturerIdInt = parseInt(manufacturerId || "0");
	const [loading, setLoading] = useState<boolean>(false);
	const [isNewManufacturer, setIsNewManufacturer] = useState<boolean>(true);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [showStockPopup, setShowStockModel] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const manufacturer = useAppSelector((state: RootState) => state.managedManufacturers);
	const stock = useAppSelector((state: RootState) => state.stocks);
	const [selectedRows, setSelectedRows] = useState<number[]>([]);

	useEffect(() => {
		dispatch(cleanManufacturersDetails());
		dispatch(getManufacturers());
		if (manufacturerId) {
			setIsNewManufacturer(false);
			dispatch(getManufacturerDetails([manufacturerIdInt]));
			dispatch(getManufacturerStock([manufacturerIdInt]));
		} else {
			setIsNewManufacturer(true);
		}
	}, [manufacturerId]);

	function handleUpdate() {
		setLoading(false);
		navigate("/app/manufacturers");
	}

	function removeManufacturingLink(){
		dispatch(importStockToManufacturers({
			filter: {
				...getStockFilterOptions(),
				ids: selectedRows,
				excludedIds: [],
			},
			manufacturer: {
				isChanging: true,
				newValue: null,
				oldValue: manufacturer.id
			}
		})).then(() => {
			dispatch(getManufacturerStock([manufacturerIdInt]));
			setShowStockModel(false);
		});
	}

	const handleSelectedRows = useCallback((pageIndex: number[]) => {
		const manufacturers = manufacturer.manufacturersStockTable;
		if(pageIndex.length == 0){
			setSelectedRows([]);
			return;
		}
		let selectedStockRows: number[] = []
		for(let i = 0; pageIndex.length > i; i++){
			selectedStockRows.push(manufacturers[pageIndex[i]].id);
		}
		setSelectedRows(selectedStockRows);
	}, [manufacturer.manufacturersStockTable]);

	function handleChange(option: any, optionValue: any) {
		dispatch(updateManufacturersField({ key: option, value: optionValue }));
	}

	function handleSubmit() {
		setShowConfirmModal(false);
		setLoading(true);
		if (checkIsDataUnique() === true) {
			if (isNewManufacturer) {
				dispatch(createManufacturer(manufacturer)).then(() => handleUpdate())
					.then(_ => { setLoading(false) });
			} else {
				dispatch(updateManufacturer(manufacturer)).then(() => handleUpdate())
					.then(_ => { setLoading(false) });
			}
		} else {
			setLoading(false);
		}
	}

	function NameValidator() {
		if (isNewManufacturer) {
			return manufacturer.table.filter(manu => manu.name === manufacturer.name).length > 0 && manufacturer.name !== "";
		}
		return manufacturer.table.filter(manu => manu.name === manufacturer.name && manu.id !== manufacturer.id).length > 0 && manufacturer.name !== "";
	}

	function CodeValidator() {
		if (manufacturer.code === "")
			return false;
		if (isNewManufacturer) {
			return manufacturer.table.filter(manu => manu.code === manufacturer.code).length > 0;
		}
		return manufacturer.table.filter(manu => manu.code === manufacturer.code && manu.id !== manufacturer.id).length > 0;
	}

	const RequiredComponent = (text: string | React.ReactNode, tooltipText: string) => {
		return (
			<ZLabel>
				<span className="d-inline-flex align-items-center gap-1 w-100">
					{text}
					<ZTooltip enabled placement='top' text={tooltipText}>
						<>*</>
					</ZTooltip>
				</span>
			</ZLabel>
		);
	}

	function checkIsDataUnique() {

		if (NameValidator() && CodeValidator()) {
			return false;
		}
		return true;
	}

	function handleCloseStockPopup() {
		setShowStockModel(false);
		dispatch(cleanStockDetails());
	}


	function managedStocksPopupSave() {		
		dispatch(importStockToManufacturers({
			filter: {
				...getStockFilterOptions(),
				ids: (stock.selectAllFilter ? [] : getRowIds(stock.selectedRows)),
				excludedIds: (stock.selectAllFilter ? getRowIds(stock.deselectedRows) : []),
			},
			manufacturer: {
				isChanging: true,
				newValue: manufacturer.id,
				oldValue: null
			}
		})).then(() => {
			dispatch(getManufacturerStock([manufacturerIdInt]));
			setShowStockModel(false);
		});
	}

	const columnsb = [
		{
			Header: "Name",
			accessor: "name",
			defaultCanSort: true,
			width: 1500,
			Cell: (row: any) => user.permissions.manufacturers.canUpdate ? <Link to={`/app/stock/${row.cell.row.original.id}`}>{row.value}</Link> : <>{row.value}</>
		},
	];

	return (
		<>
			<Title pageTitle={isNewManufacturer? "New Manufacturer" : "Update Manufacturer"} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>

									<Row>
										<h4 className="mt-2">Manufacturer Details</h4>
										<Col md={6}>
											<ZTextInput
												label={RequiredComponent("Name", "This field is required")}
												type="text"
												placeholder="Enter manufacturer name"
												value={manufacturer.name}
												key="text"
												onChange={(e) => handleChange("name", e)}
												validation={
													{
														invalid: NameValidator(),
														error: "Name needs to be unique and cant be null"
													}
												}
											/>
										</Col>
										<Col md={3}>
											<ZTextInput
												label="Code"
												type="text"
												placeholder="Enter Manufacturer code"
												value={manufacturer.code}
												key="text"
												onChange={(e) => handleChange("code", e)}
												validation={
													{
														invalid: CodeValidator(),
														error: "Code needs to be unique"
													}
												}
											/>
										</Col>
										<Col md={3}>
											<Form.Group hidden={true}>
												<Form.Label className="form-check-label">Lock</Form.Label>
												<div>
													<Form.Check
														type="switch"
														id="lock"
														label={manufacturer.locked ? "Active" : "Inactive"}
														checked={manufacturer.locked ? true : false}
														onChange={(e) => handleChange("locked", !manufacturer.locked)}														
													/>
												</div>
											</Form.Group>
										</Col>
									</Row>
									{isNewManufacturer === false && <>
										<Row >
											<Col md={12}>
												<ManagedStocksPopup show={showStockPopup} onClose={handleCloseStockPopup} onSave={managedStocksPopupSave} />
												<Table
													pageSize={15}
													isSortable={true}
													pagination={true}
													isSelectable={true}
													isSearchable={true}
													columns={columnsb}
													data={manufacturer.manufacturersStockTable}
													selectedRows={handleSelectedRows}
													errors={manufacturer.error}
													loading={manufacturer.loading}
													reloadTableRows={() => dispatch(getManufacturerStock([manufacturerIdInt]))}
													addNewRow={user.permissions.managedStock.canUpdate && !isNewManufacturer ? () => setShowStockModel(true) : undefined}
												    removeRows={user.permissions.managedStock.canDelete && !isNewManufacturer ? ()=> removeManufacturingLink() : undefined}
												/>
											</Col>
										</Row>
									</>}
									<Row className="mt-3">
										<Col sm={6}>
											<Link to="/app/manufacturers" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to All Manufacturers {" "}
											</Link>
										</Col>
										<Col sm={6} className="d-flex flex-row-reverse">
											<Button variant="primary" className="btn cta-button" disabled={loading} onClick={() => setShowConfirmModal(true)}>
												{loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
												{loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
									<Row>
										<Modal
											title="Confirm"
											show={showConfirmModal}
											buttonType="primary"
											buttonText="Continue"
											buttonCloseText="Cancel"
											handleSubmit={handleSubmit}
											handleClose={() => setShowConfirmModal(false)}
											description={
												<>
													<p>You are about to sync the Manufacturer down to all stores.</p>
													<p>To sync the stock lines within, go to Managed Stocks &gt; Sync &gt; Select Manufacturer.</p>
													<p>Are you sure you want to continue?</p>
												</>
											}
										/>
									</Row>

								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}